import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const PRIORITY_FILTERS = [
  { value: 'all', label: 'All priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const SOURCE_FILTERS = [
  { value: 'all', label: 'All sources' },
  { value: 'inbox', label: 'Inbox' },
  { value: 'budget', label: 'Budget' },
  { value: 'pacing', label: 'Pacing' },
  { value: 'zip', label: 'ZIP review' },
  { value: 'fb-push', label: 'Push to FB' },
  { value: 'manual', label: 'Manual' },
];

const WORK_DECISION_FILTERS = [
  { value: 'all', label: 'All work decisions' },
  { value: 'reply_and_task', label: 'Reply + task' },
  { value: 'task_only', label: 'Task only' },
  { value: 'needs_human_review', label: 'Needs review' },
];

const ROUTER_CATEGORY_FILTERS = [
  { value: 'all', label: 'All router categories' },
  { value: 'budget_pacing', label: 'Budget pacing' },
  { value: 'channel_performance', label: 'Channel performance' },
  { value: 'vertical_performance', label: 'Vertical performance' },
  { value: 'geo_performance', label: 'Geo performance' },
  { value: 'coverage', label: 'Coverage' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'offers', label: 'Offers' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'backend_outcomes', label: 'Backend outcomes' },
  { value: 'action_request', label: 'Action request' },
];

function TaskFilters({
  filters,
  onFiltersChange,
  statusOptions,
  clientOptions,
  assigneeOptions,
  loading,
  onReset,
}) {
  const statusFilters = useMemo(
    () => [{ value: 'all', label: 'All statuses' }, ...statusOptions],
    [statusOptions],
  );

  const handleTextChange = (field) => (event) => {
    onFiltersChange(field, event.target.value);
  };

  const handleAutocompleteChange = (field) => (_, value) => {
    onFiltersChange(field, value ? value.id : null);
  };

  const clientValue = clientOptions.find((option) => option.id === filters.client) || null;
  const assigneeValue = assigneeOptions.find((option) => option.id === filters.assignee) || null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: { xs: '100%', md: 240 } }}>
            <Autocomplete
              options={clientOptions}
              value={clientValue}
              onChange={handleAutocompleteChange('client')}
              loading={loading}
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: 240 } }}>
            <Autocomplete
              options={assigneeOptions}
              value={assigneeValue}
              onChange={handleAutocompleteChange('assignee')}
              loading={loading}
              getOptionLabel={(option) => option?.full_name || option?.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Assignee" placeholder="All assignees" />
              )}
            />
          </Box>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={handleTextChange('status')}
            sx={{ minWidth: { xs: '100%', md: 200 } }}
          >
            {statusFilters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Priority"
            value={filters.priority}
            onChange={handleTextChange('priority')}
            sx={{ minWidth: { xs: '100%', md: 180 } }}
          >
            {PRIORITY_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Source"
            value={filters.source}
            onChange={handleTextChange('source')}
            sx={{ minWidth: { xs: '100%', md: 180 } }}
          >
            {SOURCE_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Work decision"
            value={filters.sourceWorkDecision}
            onChange={handleTextChange('sourceWorkDecision')}
            sx={{ minWidth: { xs: '100%', md: 220 } }}
          >
            {WORK_DECISION_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Router category"
            value={filters.sourceRouterCategory}
            onChange={handleTextChange('sourceRouterCategory')}
            sx={{ minWidth: { xs: '100%', md: 220 } }}
          >
            {ROUTER_CATEGORY_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <DatePicker
            label="Created after"
            value={filters.createdAfter ? dayjs(filters.createdAfter) : null}
            onChange={(value) => onFiltersChange('createdAfter', value ? value.format('YYYY-MM-DD') : null)}
            slotProps={{
              textField: { size: 'medium', sx: { minWidth: { xs: '100%', md: 170 } } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Created before"
            value={filters.createdBefore ? dayjs(filters.createdBefore) : null}
            onChange={(value) => onFiltersChange('createdBefore', value ? value.format('YYYY-MM-DD') : null)}
            slotProps={{
              textField: { size: 'medium', sx: { minWidth: { xs: '100%', md: 170 } } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Due after"
            value={filters.dueAfter ? dayjs(filters.dueAfter) : null}
            onChange={(value) => onFiltersChange('dueAfter', value ? value.format('YYYY-MM-DD') : null)}
            slotProps={{
              textField: { size: 'medium', sx: { minWidth: { xs: '100%', md: 160 } } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Due before"
            value={filters.dueBefore ? dayjs(filters.dueBefore) : null}
            onChange={(value) => onFiltersChange('dueBefore', value ? value.format('YYYY-MM-DD') : null)}
            slotProps={{
              textField: { size: 'medium', sx: { minWidth: { xs: '100%', md: 160 } } },
              field: { clearable: true },
            }}
          />
          <FormControlLabel
            control={(
              <Switch
                color="primary"
                checked={filters.hideCompleted}
                onChange={(event) => onFiltersChange('hideCompleted', event.target.checked)}
              />
            )}
            label="Hide completed"
            sx={{ ml: { xs: 0, md: 2 } }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" onClick={onReset} sx={{ mt: { xs: 1, md: 0 } }}>
            Reset
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

TaskFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    client: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    assignee: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    status: PropTypes.string,
    priority: PropTypes.string,
    source: PropTypes.string,
    sourceWorkDecision: PropTypes.string,
    sourceRouterCategory: PropTypes.string,
    createdAfter: PropTypes.string,
    createdBefore: PropTypes.string,
    dueAfter: PropTypes.string,
    dueBefore: PropTypes.string,
    hideCompleted: PropTypes.bool,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  clientOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  assigneeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
};

TaskFilters.defaultProps = {
  loading: false,
};

export default TaskFilters;
